import React, { useEffect } from "react";
import { Dialog, DialogContent, DialogActions, Button, Box, Typography } from "@mui/material";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

// The same look as every other popup of the site (UiComponents/Scss/_swal.scss): a white card, a ring icon, a navy title
// with a short bar in the icon's colour, grey text and a navy OK button.
const NAVY = "#1E3A5F";
const NAVY_LIGHT = "#2E5A8F";
const SUCCESS = "#12B76A";
const ERROR = "#F04438";

/** A success message closes by itself after this long (the site's default for message popups); an error stays until OK. */
export const STATUS_AUTO_CLOSE_MS = 3000;

const StatusResultDialog = ({ open, onClose, type, message }) => {
    const isSuccess = type === "success";
    const accent = isSuccess ? SUCCESS : ERROR;

    useEffect(() => {
        if (!open || !isSuccess || !onClose) return undefined;
        const timer = setTimeout(onClose, STATUS_AUTO_CLOSE_MS);
        return () => clearTimeout(timer);
    }, [open, isSuccess, onClose]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xs"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: "16px",
                    overflow: "hidden",
                    maxWidth: 420,
                    boxShadow: "0 24px 48px -12px rgba(16, 24, 40, 0.28), 0 0 0 1px rgba(16, 24, 40, 0.04)",
                },
            }}
            BackdropProps={{ sx: { backgroundColor: "rgba(16, 24, 40, 0.55)", backdropFilter: "blur(2px)" } }}
        >
            <DialogContent sx={{ padding: "32px 28px 8px", textAlign: "center" }}>
                <Box
                    sx={{
                        width: 80,
                        height: 80,
                        boxSizing: "border-box",
                        borderRadius: "50%",
                        border: `4px solid ${accent}40`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        margin: "0 auto 18px",
                    }}
                >
                    {isSuccess ? (
                        <CheckRoundedIcon sx={{ fontSize: 46, color: accent }} />
                    ) : (
                        <CloseRoundedIcon sx={{ fontSize: 46, color: accent }} />
                    )}
                </Box>
                <Typography
                    sx={{
                        fontFamily: "Poppins, sans-serif",
                        fontWeight: 600,
                        fontSize: "18px",
                        lineHeight: 1.35,
                        color: NAVY,
                        "&::after": {
                            content: '""',
                            display: "block",
                            width: 36,
                            height: 3,
                            margin: "10px auto 0",
                            borderRadius: "3px",
                            backgroundColor: accent,
                        },
                    }}
                >
                    {isSuccess ? "Success!" : "Error"}
                </Typography>
                <Typography
                    sx={{
                        fontFamily: "Poppins, sans-serif",
                        fontSize: "14px",
                        color: "#475467",
                        lineHeight: 1.55,
                        marginTop: "14px",
                        wordBreak: "break-word",
                    }}
                >
                    {message}
                </Typography>
            </DialogContent>
            <DialogActions sx={{ padding: "16px 20px 22px", justifyContent: "center" }}>
                <Button
                    onClick={onClose}
                    sx={{
                        textTransform: "none",
                        fontFamily: "Poppins, sans-serif",
                        fontWeight: 500,
                        fontSize: "13px",
                        minWidth: 96,
                        padding: "9px 22px",
                        borderRadius: "8px",
                        backgroundColor: NAVY,
                        color: "#fff",
                        boxShadow: "none",
                        "&:hover": { backgroundColor: NAVY_LIGHT, boxShadow: "none" },
                        "&:focus-visible": { boxShadow: "0 0 0 3px rgba(46, 90, 143, 0.28)" },
                    }}
                >
                    OK
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default StatusResultDialog;
